import { useEffect, useState } from "react";
import { Mail, Phone, Send, Loader2, History, Pause, Play } from "lucide-react";
import { api } from "../../lib/api";
import { CustomerDetailDialog } from "./CustomerDetailDialog";

function initials(name) {
  return (name || "").trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_DOT = {
  sent: "bg-emerald-400",
  skipped: "bg-white/30",
  failed: "bg-red-400",
};

export function FollowUpsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/followups")
      .then((res) => setRows(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const runNow = async (leadId) => {
    setRunningId(leadId);
    try {
      await api.post(`/admin/followups/${leadId}/run-now`);
      load();
    } catch {
      // no-op, list stays as-is
    } finally {
      setRunningId(null);
    }
  };

  const toggleEnabled = async (lead) => {
    setRows((prev) => prev.map((r) => (r.id === lead.id ? { ...r, follow_up_enabled: !r.follow_up_enabled } : r)));
    await api.patch(`/admin/leads/${lead.id}`, { followUpEnabled: !lead.follow_up_enabled }).catch(() => load());
  };

  return (
    <div data-testid="admin-followups-tab">
      <div className="mb-8">
        <div className="kb-label mb-3">Relationship</div>
        <h1 className="font-display text-3xl font-bold text-white">Follow Us</h1>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">
          Every enquiry gets an AI-drafted weekly check-in for up to {rows[0]?.max_follow_ups || 4} weeks, sent automatically by email when one is on file.
          Leads with a phone number only are queued here for manual outreach. Click a row for full details.
        </p>
      </div>

      <div className="kb-glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="px-5 py-4 font-medium">Customer</th>
                <th className="px-5 py-4 font-medium">Contact</th>
                <th className="px-5 py-4 font-medium">Progress</th>
                <th className="px-5 py-4 font-medium">Next Follow-up</th>
                <th className="px-5 py-4 font-medium">Last Message</th>
                <th className="px-5 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-white/40">
                    No enquiries with contact details yet.
                  </td>
                </tr>
              ) : null}
              {rows.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => setSelectedId(l.id)}
                  className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] cursor-pointer"
                  data-testid={`followup-row-${l.id}`}
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center text-xs font-semibold text-[#d4a840] shrink-0">
                        {initials(l.name)}
                      </div>
                      <div className="text-white font-medium">{l.name}</div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="space-y-1">
                      {l.email ? (
                        <div className="flex items-center gap-2 text-white/70 text-xs">
                          <Mail size={12} className="text-[#d4a840]" /> {l.email}
                        </div>
                      ) : null}
                      {l.phone ? (
                        <div className="flex items-center gap-2 text-white/70 text-xs">
                          <Phone size={12} className="text-[#d4a840]" /> {l.phone}
                        </div>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-white/70 text-xs">
                    {l.follow_up_count} / {l.max_follow_ups} sent
                    {!l.follow_up_enabled ? <span className="ml-2 text-white/40">(paused)</span> : null}
                  </td>
                  <td className="px-5 py-4 text-white/70 text-xs">{l.follow_up_enabled ? fmt(l.next_follow_up_at) : "—"}</td>
                  <td className="px-5 py-4 max-w-xs">
                    {l.last_follow_up ? (
                      <div className="flex items-center gap-2">
                        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${STATUS_DOT[l.last_follow_up.status] || "bg-white/30"}`} />
                        <span className="text-white/60 text-xs truncate">{l.last_follow_up.message}</span>
                      </div>
                    ) : (
                      <span className="text-white/30 text-xs">No messages yet</span>
                    )}
                  </td>
                  <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedId(l.id)}
                        title="View details"
                        data-testid={`followup-view-history-${l.id}`}
                        className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-white hover:border-[#d4a840]/40 transition-colors"
                      >
                        <History size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleEnabled(l)}
                        title={l.follow_up_enabled ? "Pause follow-ups" : "Resume follow-ups"}
                        data-testid={`followup-toggle-${l.id}`}
                        className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-white hover:border-[#d4a840]/40 transition-colors"
                      >
                        {l.follow_up_enabled ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => runNow(l.id)}
                        disabled={runningId === l.id}
                        data-testid={`followup-send-now-${l.id}`}
                        className="kb-btn-ghost !py-2 !px-3 text-xs disabled:opacity-60"
                      >
                        {runningId === l.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        Send now
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CustomerDetailDialog
        lead={rows.find((r) => r.id === selectedId) || null}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}
